import React, { useState } from "react";

/**
 * Crowther · brand primitives (v2 spec, Part 4).
 *
 * Every primitive here reads only from `--c-*`, `--type-*`, `--s-*`,
 * `--radius-*`, `--motion-*` tokens declared in `/app/frontend/src/tokens.css`.
 * No local hex codes, no bespoke sizes. Consumers style ground-context by
 * setting the CSS variable `--ink` on a parent (or by passing `tone`).
 *
 * British English, no contractions, no em/en dashes — enforce at the call
 * site (this file exposes no default copy strings).
 */

/* ------------------------------------------------------------------
   WordmarkChip — Part 4.1
   The wordmark is never placed directly on Midnight Forest. On any
   dark ground it sits inside a Linen chip: rounded rectangle, 8 px
   radius, padding 10 × 16. On light grounds the chip has no fill and
   the mark sits directly.
   ------------------------------------------------------------------ */

export function WordmarkChip({ tone = "auto", size = 30, alt = "Crowther", className = "" }) {
  // tone: "light" | "dark" | "auto"
  const dark = tone === "dark";
  const chipStyle = dark
    ? {
        background: "var(--c-linen)",
        padding: "10px 16px",
        borderRadius: "var(--radius-chip)",
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 0,
      }
    : {
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 0,
      };
  return (
    <span className={`cw-wordmark-chip ${className}`} data-tone={dark ? "dark" : "light"} style={chipStyle}>
      <img
        src="/logos/wordmark-black-fit.png"
        alt={alt}
        style={{ height: `${size}px`, width: "auto", display: "block" }}
      />
    </span>
  );
}

/* ------------------------------------------------------------------
   Eyebrow — Part 3.1 type-eyebrow scale
   Optional leading ■ Seafoam glyph (Part 4.2).
   ------------------------------------------------------------------ */

export function Eyebrow({ children, glyph = false, as: Tag = "div", className = "" }) {
  return (
    <Tag
      className={`cw-eyebrow-v2 ${className}`}
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: "var(--type-eyebrow-size)",
        lineHeight: "var(--type-eyebrow-lh)",
        letterSpacing: "var(--type-eyebrow-tracking)",
        textTransform: "uppercase",
        fontWeight: 500,
        color: "currentColor",
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--s-2)",
      }}
    >
      {glyph && (
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            background: "var(--c-seafoam)",
          }}
        />
      )}
      <span>{children}</span>
    </Tag>
  );
}

/* ------------------------------------------------------------------
   Pill — primary / ghost / link
   Copy must always be provided verbatim by the call site.
   ------------------------------------------------------------------ */

export function Pill({
  as: Tag = "a",
  children,
  variant = "primary", // primary | ghost | link
  tone = "light",       // light | dark  (background context)
  className = "",
  ...rest
}) {
  const [hovered, setHovered] = useState(false);
  const base = {
    fontFamily: "var(--font-sans)",
    fontSize: "14.5px",
    fontWeight: 300,
    letterSpacing: "-0.003em",
    borderRadius: "var(--radius-pill)",
    padding: "0 var(--s-5)",
    height: "48px",
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--s-2)",
    border: `${"var(--hairline)"} solid transparent`,
    whiteSpace: "nowrap",
    transition:
      "background-color var(--motion-fast-duration) var(--motion-fast-ease), " +
      "color var(--motion-fast-duration) var(--motion-fast-ease), " +
      "border-color var(--motion-fast-duration) var(--motion-fast-ease)",
    cursor: "pointer",
    textDecoration: "none",
  };

  let variantStyle;
  if (variant === "primary") {
    variantStyle = hovered
      ? { background: "var(--c-seafoam)", color: "var(--c-forest)" }
      : { background: "var(--c-forest)",  color: "#FFFFFF" };
  } else if (variant === "ghost") {
    variantStyle = {
      background: hovered ? (tone === "dark" ? "rgba(246,241,234,0.06)" : "rgba(22,30,27,0.04)") : "transparent",
      color: tone === "dark" ? "var(--c-linen)" : "var(--c-forest)",
      borderColor: tone === "dark" ? "var(--c-hairline-on-dark)" : "var(--c-hairline-on-light)",
    };
  } else {
    // link
    variantStyle = {
      background: "transparent",
      color: hovered
        ? "var(--c-seafoam)"
        : (tone === "dark" ? "var(--c-linen)" : "var(--c-forest)"),
      padding: "0",
      height: "auto",
      borderRadius: 0,
      borderBottom: `${"var(--hairline)"} solid currentColor`,
      paddingBottom: "2px",
    };
  }
  return (
    <Tag
      className={`cw-pill-v2 cw-pill-${variant} ${className}`}
      style={{ ...base, ...variantStyle }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------
   Card — Linen-4/Linen-8 on dark, Forest-4/Forest-8 on light
   ------------------------------------------------------------------ */

export function Card({ children, tone = "light", padded = true, className = "", ...rest }) {
  const dark = tone === "dark";
  const style = {
    background: dark ? "var(--c-surface-on-dark-4)" : "var(--c-surface-on-light-4)",
    border: `${"var(--hairline)"} solid ${dark ? "var(--c-hairline-on-dark)" : "var(--c-hairline-on-light)"}`,
    borderRadius: "var(--radius-card)",
    padding: padded ? "var(--s-6)" : 0,
    color: "currentColor",
  };
  return (
    <div className={`cw-card-v2 ${className}`} data-tone={dark ? "dark" : "light"} style={style} {...rest}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------
   FigPanel — Part 4.6 (skeleton).
   Numbered technical panel with monospace body. Consumers pass in
   `n` (figure number), `label` (eyebrow text) and children (content).
   ------------------------------------------------------------------ */

export function FigPanel({ n = 1, label, tone = "light", children, className = "" }) {
  const dark = tone === "dark";
  return (
    <div
      className={`cw-fig-panel-v2 ${className}`}
      data-tone={dark ? "dark" : "light"}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "12.5px",
        lineHeight: 1.55,
        color: dark ? "var(--c-linen)" : "var(--c-forest)",
        background: dark ? "var(--c-surface-on-dark-4)" : "var(--c-surface-on-light-4)",
        border: `${"var(--hairline)"} solid ${dark ? "var(--c-hairline-on-dark)" : "var(--c-hairline-on-light)"}`,
        borderRadius: "var(--radius-card)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--s-4)",
          padding: "var(--s-3) var(--s-4)",
          borderBottom: `${"var(--hairline)"} dashed ${dark ? "var(--c-hairline-on-dark)" : "var(--c-hairline-on-light)"}`,
          color: dark ? "var(--c-text-muted-on-dark)" : "var(--c-text-muted-on-light)",
          fontSize: "10.5px",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        <span>FIG.{n}{label ? ` · ${label}` : ""}</span>
      </div>
      <div style={{ padding: "var(--s-4) var(--s-5) var(--s-5)" }}>{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------
   AudienceSwitch — the cross-side nav link that appears on both
   navs (Part 7.1 machines nav, Part 8.1 enterprise nav). Reused so
   both sides receive an identical control.
   ------------------------------------------------------------------ */

export function AudienceSwitch({ to, label, tone = "light", className = "" }) {
  const dark = tone === "dark";
  return (
    <a
      href={to}
      className={`cw-audience-switch ${className}`}
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: "13.5px",
        color: dark ? "var(--c-text-muted-on-dark)" : "var(--c-text-muted-on-light)",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        textDecoration: "none",
        transition: "color var(--motion-fast-duration) var(--motion-fast-ease)",
      }}
      data-testid="audience-switch"
    >
      <span>{label}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </a>
  );
}
